// Catalogue module — the data-driven SaaS lookup, pricing model, and noise list.
//
// The catalogue is no longer hard-coded: it is fetched from the backend at
// startup and installed via setCatalogue(). Detection (detectSaaS / isNoiseHost),
// pricing lookup (priceFor), and the pure pricing/host helpers all live here.
// Per-row detection still runs in the browser — the catalogue is loaded once and
// these functions read from module state.

import {
  extractHost, rootHost,
  isNoiseHost as coreIsNoiseHost,
  detectSaaS as coreDetectSaaS,
} from '../shared/detection-core.js';

// ── Module state (populated by setCatalogue) ─────────────────────────────────
let PRODUCTS = [];          // [{ id, name, category, match:[domains], price }]
let NOISE_HOSTS = [];       // host strings
let NOISE_PATTERNS = [];    // compiled RegExp
let NOISE_SET = new Set();
let NOISE_STATE = { noiseSet: NOISE_SET, noiseHosts: NOISE_HOSTS, noisePatterns: NOISE_PATTERNS };
let PRICE_BY_NAME = new Map();

// Install catalogue data fetched from the API. Shape:
//   { products: [{ id, name, category, price, domains:[{id,domain}]|[string] }],
//     noise:    [{ id, value, kind:'host'|'pattern' }] }
function setCatalogue({ products = [], noise = [] } = {}) {
  PRODUCTS = products.map((p) => ({
    id: p.id,
    name: p.name,
    category: p.category || 'Other',
    match: (p.domains || p.match || []).map((d) => (typeof d === 'string' ? d : d.domain)),
    price: p.price || null,
  }));

  NOISE_HOSTS = [];
  NOISE_PATTERNS = [];
  for (const n of noise) {
    if (n.kind === 'pattern') {
      try { NOISE_PATTERNS.push(new RegExp(n.value)); } catch { /* skip invalid */ }
    } else {
      NOISE_HOSTS.push(n.value);
    }
  }
  NOISE_SET = new Set(NOISE_HOSTS);
  NOISE_STATE = { noiseSet: NOISE_SET, noiseHosts: NOISE_HOSTS, noisePatterns: NOISE_PATTERNS };
  PRICE_BY_NAME = new Map(PRODUCTS.map((p) => [p.name, p.price || null]));
}

function getProducts() { return PRODUCTS; }

// ── Detection (delegates to the shared, env-neutral core) ────────────────────
function isNoiseHost(host) { return coreIsNoiseHost(host, NOISE_STATE); }
function detectSaaS(siteName) { return coreDetectSaaS(siteName, PRODUCTS); }

// Lookup price by canonical product name
function priceFor(name) { return PRICE_BY_NAME.get(name) || null; }

// ── Pricing helpers (pure) ───────────────────────────────────────────────────
function formatPrice(price) {
  if (!price) return { text: '—', sub: 'Pricing unknown', unit: '' };
  const unit = price.unit === 'flat' ? '/mo' : '/user/mo';
  if (price.min === 0 && price.max === 0) return { text: 'Free', sub: price.notes || 'No cost', unit };
  if (price.min === 0) return { text: `£0–£${price.max.toFixed(2)}`, sub: price.notes || `Free tier · up to £${price.max.toFixed(2)}${unit}`, unit };
  if (price.min === price.max) return { text: `£${price.min.toFixed(2)}`, sub: price.notes || `${unit}`, unit };
  return { text: `£${price.min.toFixed(2)}–£${price.max.toFixed(2)}`, sub: price.notes || unit, unit };
}

function estimateMonthly(price, users) {
  if (!price) return null;
  if (price.unit === 'flat') return { min: price.min, max: price.max };
  const u = Math.max(0, users || 0);
  return { min: price.min * u, max: price.max * u };
}

function fmtGBP(n) {
  if (n == null || isNaN(n)) return '—';
  if (n === 0) return '£0';
  if (n < 1) return `£${n.toFixed(2)}`;
  if (n < 1000) return `£${Math.round(n).toLocaleString()}`;
  if (n < 1e6) return `£${(n / 1000).toFixed(n < 10000 ? 1 : 0)}k`;
  return `£${(n / 1e6).toFixed(2)}M`;
}

export {
  setCatalogue, getProducts,
  detectSaaS, isNoiseHost, priceFor,
  extractHost, rootHost,
  formatPrice, estimateMonthly, fmtGBP,
};
